import { isEmpty } from 'lodash';

import { convertRemToPx } from '@hivebrite/design-system/Theme';

import { getColumnWidthByTemplate } from 'hb-builder/components/BuilderComponents/page/block/Partners/utils/getItemArguments';
import { type BuilderComponent } from 'hb-builder/types';
import { createNewItem } from 'hb-builder/utils/createNewItem';
import { type PageSchemaBuilder } from 'hb-builder/utils/migrate/schemas/types';
import { convertPercentInPixels } from 'hb-builder/utils/migrate/utils/convertPercentInPixels';
import { getNewButton } from 'hb-builder/utils/migrate/utils/getNewButton';
import { transformContainerPadding } from 'hb-builder/utils/migrate/utils/transformContainerPadding';
import { type DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';

export const needMigration = (doc: DocumentItem, date: string) =>
  !doc?.attributes?.lastSave ||
  new Date(doc?.attributes?.lastSave).getTime() < new Date(date).getTime();

const schemas = [
  // Partner component now specifies its column width responsive values
  ({ document }: PageSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2022-08-15')) {
      return {
        Partner: {
          columnWidth: (_, attrs) => {
            if (!attrs.template) {
              return {
                _: () => 1,
                m: () => 0.5,
                xl: () => 0.3333333333333333,
              };
            }
          },
        },
      };
    }

    return null;
  },

  // migrate remainder components called Image following first isolation failure
  ({ document }: PageSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2025-11-20')) {
      return {
        Image: {
          name: () => 'PageImage',
          type: () => 'PageImage',
        },
        PageImage: {
          name: () => 'PageImage',
          type: () => 'PageImage',
        },
      };
    }

    return null;
  },

  // migrate old Partners version to new Partners Template One
  ({
    document,
    components,
    config,
    theme,
  }: PageSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2023-02-01')) {
      return {
        Partners: {
          attributes: () => ({
            containerPadding: () => '30px 0px 30px 0px',
            height: () => undefined,
          }),
          children: (partners) => {
            if (!components) {
              return partners;
            }

            const hiddenHeadline = components.Text.item({
              features: {
                ...config,
                text: {
                  ...config?.text,
                  containerPadding: '0px 0px 0px 0x',
                  content: __('Meet our great partners'),
                  hidden: true,
                  horizontalAlign: 'center',
                  textFontSize: convertRemToPx(
                    parseInt(theme.fontSizes[9], 10),
                  ),
                  tied: true,
                },
              },
              theme,
            });

            const partnersGrid = components.PartnersGrid.item({
              attributes: { containerPadding: '0px 0px 0px 0px' },
              components,
              children: partners.map((partner, index) =>
                components.Partner.item({
                  columnWidth: {
                    _: 0.5,
                    m: 0.3333333333333333,
                    l: 0.25,
                    xl: 0.1666666667,
                  },
                  index,
                  logoBackground: 'transparent',
                  openInNewTab: partner.attributes.openInNewTab,
                  partnersCount: partners.length,
                  partnerImage: partner.attributes.partnerImage,
                  partnerImageAlt: partner.partnerImageAlt,
                  partnerImageUrl: partner.partnerImageUrl,
                  template: 'PartnersTemplateOne',
                }),
              ),
            });

            return [
              components.Column.item({
                disableSelection: true,
                children: [hiddenHeadline, partnersGrid],
              }),
            ];
          },
          name: () => 'PartnersTemplateOne',
          type: () => 'PartnersTemplateOne',
        },
      };
    }

    return null;
  },

  // migrate Hero component to GridSection mode
  ({ document }: PageSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2023-02-01')) {
      return {
        Hero: {
          attributes: () => ({
            hasColGap: () => true,
            numCols: () => ({
              s: () => 1,
              m: () => 1,
              l: () => 1,
              xl: () => 1,
            }),
          }),
        },
      };
    }

    return null;
  },

  // migrate dynamic Slider component from type event to type events (error typo)
  ({ document }: PageSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2023-02-01')) {
      return {
        Slider: {
          attributes: () => ({
            dynamicAttributes: () => ({
              type: (oldType) => (oldType === 'event' ? 'events' : oldType),
            }),
          }),
        },
      };
    }

    return null;
  },

  // add profileCompletion to  UserBanner
  ({
    document,
    config,
    components,
    theme,
  }: PageSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2023-05-24')) {
      return {
        UserBanner: {
          children: (children) => {
            const { length } = children;

            if (components && length < 7) {
              const profileCompletionChild = components.Text.item({
                features: {
                  ...config,
                  text: {
                    ...config?.text,
                    containerPadding: 0,
                    content: __('Profile completed %{percentage}%').i__({
                      percentage: '0',
                    }),
                    horizontalAlign: 'left',
                    textColor: '#555555',
                    textFontSize: convertRemToPx(
                      parseInt(theme.fontSizes[2], 10),
                    ),
                    tied: true,
                    hidden: true,
                  },
                },
                theme,
              });

              return [...children, profileCompletionChild];
            }

            return children;
          },
        },
      };
    }

    return null;
  },

  ({ components, document }: PageSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2023-10-01')) {
      return {
        TemplateWithPlainImagesStripsItem: {
          children: (children) => {
            const { length } = children;

            if (components && length === 4) {
              const [image, headline, subline, button] = children;
              const wrapper = components.Wrapper.item({
                attributes: {
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                },
                children: [createNewItem(headline), createNewItem(subline)],
              });

              return [image, wrapper, button];
            }

            return children;
          },
        },
      };
    }

    return null;
  },

  // missing secondary variant on third button of User Banner
  ({ document }: PageSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2023-08-01')) {
      return {
        UserBanner: {
          children: (children) => {
            // needed to apply other button attributes to the one broken (Porfolio)
            let buttonAttributes;

            return children.reduce((acc, curr) => {
              if (curr.type !== 'Button') {
                return [...acc, curr];
              }

              const { variant, ...otherButtonAttributes } =
                curr.attributes || {};

              if (!isEmpty(otherButtonAttributes)) {
                buttonAttributes = curr.attributes;
              }

              const newButton = {
                ...curr,
                attributes: {
                  ...buttonAttributes,
                  variant: 'secondary',
                },
              };

              return [...acc, newButton];
            }, []);
          },
        },
      };
    }

    return null;
  },

  // Merged attributes: tied (component not draggable) and blockEdition (admin cannot update text outside panel).
  ({ document }: PageSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2023-09-01')) {
      return {
        Text: () => ({
          attributes: (attributes) => {
            delete attributes.blockEdition;

            return attributes;
          },
        }),
      };
    }

    return null;
  },

  // Remove hardcoded value of JSON when button has a variant
  ({ document }: PageSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2023-09-01')) {
      return {
        UserBanner: {
          children: (children) =>
            children.reduce((acc, curr) => {
              if (curr.type === 'Button') {
                delete curr.attributes?.buttonBackgroundColor;
                delete curr.attributes?.buttonColor;
              }

              return [...acc, curr];
            }, []),
        },
      };
    }

    return null;
  },
  ({ document }: PageSchemaBuilder<BuilderComponent>) => {
    // Add category to all single components
    if (needMigration(document, '2023-12-01')) {
      return {
        PageImage: {
          category: () => 'single',
        },
        Video: {
          category: () => 'single',
        },
      };
    }

    return null;
  },

  ({ document }: PageSchemaBuilder<BuilderComponent>) => {
    const getNewText = (item, textColor = '#000') => ({
      ...item,
      attributes: {
        ...item.attributes,
        ...(item.attributes.textColor ? {} : { textColor }),
        defaultTextColor: true,
      },
    });

    const updatePartnersColumns = (columns) =>
      columns.map((column) => {
        const columnChildren = column.children.reduce((acc, columnChild) => {
          if (columnChild.type === 'Text') {
            const newText = getNewText(columnChild);

            return [...acc, newText];
          }

          return [...acc, columnChild];
        }, []);

        column.children = columnChildren;

        return column;
      });

    const updateStripsTemplate = (stripsSlider) =>
      stripsSlider.map((slider) => {
        const sliderChildren = slider.children.map((item) => {
          const itemChildren = item.children.reduce((acc, itemChild) => {
            if (itemChild.type === 'Text') {
              const newText = getNewText(itemChild);

              return [...acc, newText];
            }

            return [...acc, itemChild];
          }, []);

          item.children = itemChildren;

          return item;
        });

        slider.children = sliderChildren;

        return slider;
      });

    const updateStripsTemplatePlainImages = (stripsSlider) =>
      stripsSlider.map((slider) => {
        const sliderChildren = slider.children.map((slide) => {
          const slideChildren = slide.children.reduce((slideAcc, slideItem) => {
            if (slideItem.type === 'Wrapper') {
              const slideItemChildren = slideItem.children.reduce(
                (acc, item) => {
                  if (item.type === 'Text') {
                    const newText = getNewText(item);

                    return [...acc, newText];
                  }

                  return [...acc, item];
                },
                [],
              );

              slideItem.children = slideItemChildren;
            }

            return [...slideAcc, slideItem];
          }, []);

          slide.children = slideChildren;

          return slide;
        });

        slider.children = sliderChildren;

        return slider;
      });

    const updateHeroComponent = (columns) => {
      const columnsChildren = columns.map((column) => {
        const columnChildren = column.children.map((component) => {
          if (component.type === 'Text') {
            const newText = getNewText(component, '#fff');

            return newText;
          }

          return component;
        });

        column.children = columnChildren;

        return column;
      });

      columns.children = columnsChildren;

      return columns;
    };

    // Add an attribute to the Text component that is inside a Section with a default background color (Partners, Strips, UserBanner).
    // Without this migration, the text color defaults to the font color (font brightness) defined in the custom theme font.
    // To preserve the previously created documents, we need to specify that we want to apply the textColor set in the JSON.
    if (needMigration(document, '2023-12-01')) {
      return {
        PartnersTemplateOne: {
          children: updatePartnersColumns,
        },
        PartnersTemplateTwo: {
          children: updatePartnersColumns,
        },
        PartnersTemplateThree: {
          children: updatePartnersColumns,
        },
        PartnersTemplateFour: {
          children: updatePartnersColumns,
        },
        StripsTemplateWithButton: {
          children: updateStripsTemplate,
        },
        StripsTemplateWithLongText: {
          children: updateStripsTemplate,
        },
        StripsTemplateWithPlainImages: {
          children: updateStripsTemplatePlainImages,
        },
        Hero: {
          children: updateHeroComponent,
        },
        HeroSlider: {
          children: updateHeroComponent,
        },
        UserBanner: {
          children: (children) =>
            children.reduce((acc, curr) => {
              if (curr.type === 'Text') {
                const newText = getNewText(curr);

                return [...acc, newText];
              }

              return [...acc, curr];
            }, []),
        },
      };
    }

    return null;
  },

  // We made a POC to transform paddings from pixels to %.
  // We did not validate it and want to remove the changes.
  ({ document }: PageSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2023-12-31')) {
      const { bodyWidth: rawBodyWidth } =
        document.children?.[0]?.attributes || {};
      const bodyWidth = typeof rawBodyWidth === 'number' ? rawBodyWidth : 1440;

      return {
        Section: {
          attributes: () => ({
            containerPadding: (padding) => {
              if (padding?.includes('%')) {
                return convertPercentInPixels(bodyWidth, padding);
              }

              return padding;
            },
          }),
          children: (columns) =>
            columns.map((column) =>
              transformContainerPadding(column, '100%', bodyWidth),
            ),
        },
      };
    }

    return null;
  },
  ({ document }: PageSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2024-01-15')) {
      return {
        StripImage: {
          alt: () => undefined,
          attributes: (_, item) => ({
            alt: (alt) => alt || item.alt || '',
          }),
        },
      };
    }

    return null;
  },

  // remove top post from lobby template for topics
  ({
    document,
    topic,
  }: PageSchemaBuilder<BuilderComponent> & { topic?: number }) => {
    if (needMigration(document, '2024-03-01')) {
      return {
        Slider: {
          attributes: () => ({
            dynamicAttributes: () => ({
              query: () => ({
                dataPostsType: (oldDataPostsType) =>
                  topic ? 'last_created' : oldDataPostsType,
              }),
            }),
          }),
        },
      };
    }

    return null;
  },

  // We are deprecating the 'inversed' Button attribute and replacing it with the 'variant'.
  // Additionally, old buttons used to fallback to theme values if attributes were missing. We now explicitly specify each attribute.
  ({ document, theme }: PageSchemaBuilder<BuilderComponent>) => {
    if (!theme || !theme.colors || !theme.themeVariables || !theme.buttons) {
      return null;
    }

    const updateStripsTemplate = (stripsSlider) =>
      stripsSlider.map((slider) => {
        const sliderChildren = slider.children.map((item) => {
          const itemChildren = item.children.reduce((acc, itemChild) => {
            if (itemChild.type === 'Button') {
              const newButton = getNewButton(
                itemChild,
                false,
                itemChild.attributes?.mode !== 'network',
                theme,
              );

              return [...acc, newButton];
            }

            return [...acc, itemChild];
          }, []);

          item.children = itemChildren;

          return item;
        });

        slider.children = sliderChildren;

        return slider;
      });

    const updateStripsTemplatePlainImages = (stripsSlider) =>
      stripsSlider.map((slider) => {
        const sliderChildren = slider.children.map((slide) => {
          const slideChildren = slide.children.reduce((acc, item) => {
            if (item.type === 'Button') {
              const newButton = getNewButton(
                item,
                item.attributes.inversed,
                item.attributes?.mode !== 'network',
                theme,
              );

              return [...acc, newButton];
            }

            return [...acc, item];
          }, []);

          slide.children = slideChildren;

          return slide;
        });

        slider.children = sliderChildren;

        return slider;
      });

    if (needMigration(document, '2024-03-31')) {
      return {
        StripsTemplateWithButton: {
          children: updateStripsTemplate,
        },
        StripsTemplateWithLongText: {
          children: updateStripsTemplate,
        },
        StripsTemplateWithPlainImages: {
          children: updateStripsTemplatePlainImages,
        },
      };
    }

    return null;
  },

  // Migrate the Button components to Buttons (as a single custom button child of Buttons)
  ({ components, document, theme }: PageSchemaBuilder<BuilderComponent>) => {
    if (!theme || !theme.colors || !theme.themeVariables || !theme.buttons) {
      return null;
    }

    const formatButtonAsChildren = (item) => {
      const mode = item.attributes.mode || 'custom';
      const isCustom = mode === 'custom';

      const newButton = getNewButton(item, false, isCustom, theme);

      const buttonAttributesOnly = { ...newButton.attributes };

      delete buttonAttributesOnly.containerBackgroundColor;
      delete buttonAttributesOnly.containerPadding;
      delete buttonAttributesOnly.horizontalAlign;

      return [
        {
          ...newButton,
          attributes: {
            ...buttonAttributesOnly,
            hidden: undefined,
            tied: true,
          },
          name: 'Button',
          type: 'Button',
        },
      ];
    };

    const migrateButtonToButtons = (buttons, item, tied) => ({
      category: buttons.componentCategory,
      children: formatButtonAsChildren(item),
      name: buttons.componentName,
      type: buttons.componentName,
      attributes: {
        childrenSpacing: 'medium',
        containerBackgroundColor:
          item.attributes.containerBackgroundColor || '',
        containerPadding:
          item.attributes.containerPadding || '10px 10px 10px 10px',
        hidden: item.attributes.hidden || false,
        horizontalAlign: item.attributes.horizontalAlign || 'center',
        tied,
      },
    });

    const updateColumnsChildren = (buttons, columnChildren, tied) =>
      columnChildren.map((child) => {
        if (child.type === 'Button') {
          return migrateButtonToButtons(buttons, child, tied);
        }

        return child;
      });

    const updateSectionsChildren = (buttons, sectionChildren) =>
      sectionChildren.map((child) => {
        if (child.type === 'Column') {
          return {
            ...child,
            children: updateColumnsChildren(buttons, child.children, false),
          };
        }

        return child;
      });

    const updateHerosChildren = (buttons, heroChildren) =>
      heroChildren.map((child) => {
        if (child.type === 'Column') {
          return {
            ...child,
            children: updateColumnsChildren(buttons, child.children, true),
          };
        }

        return child;
      });

    if (needMigration(document, '2024-03-31') && components?.Buttons) {
      return {
        Section: {
          children: (sectionChildren) =>
            updateSectionsChildren(components.Buttons, sectionChildren),
        },
        Hero: {
          children: (sectionChildren) =>
            updateHerosChildren(components.Buttons, sectionChildren),
        },
      };
    }

    return null;
  },

  // Following a bug some partner items are missing the template & column width values
  ({ document }: PageSchemaBuilder<BuilderComponent>) => {
    const TEMPLATES_WITH_WHITE_BACKGROUND = [
      'PartnersTemplateTwo',
      'PartnersTemplateThree',
      'PartnersTemplateFour',
    ];
    const updateLogos = (children, template) =>
      children.map((child) => {
        if (child.type === 'Partner' && !child.template) {
          return {
            ...child,
            attributes: {
              ...child.attributes,
              columnWidth:
                child.columnWidth || getColumnWidthByTemplate(template),
              logoBackground: TEMPLATES_WITH_WHITE_BACKGROUND.includes(template)
                ? 'white'
                : 'transparent',
            },
            template,
          };
        }

        return child;
      });

    const updateColumnsChildren = (columnChildren, template) =>
      columnChildren.map((child) => {
        if (
          child.type === 'PartnersGrid' ||
          child.type === 'PartnersShifted' ||
          child.type === 'PartnersSlider'
        ) {
          return {
            ...child,
            children: updateLogos(child.children, template),
          };
        }

        return child;
      });

    const updatePartnersChildren = (partnersChildren, template) =>
      partnersChildren.map((child) => {
        if (child.type === 'Column') {
          return {
            ...child,
            children: updateColumnsChildren(child.children, template),
          };
        }

        return child;
      });

    if (needMigration(document, '2024-03-15')) {
      return {
        PartnersTemplateOne: {
          children: (children) =>
            updatePartnersChildren(children, 'PartnersTemplateOne'),
        },
        PartnersTemplateTwo: {
          children: (children) =>
            updatePartnersChildren(children, 'PartnersTemplateTwo'),
        },
        PartnersTemplateThree: {
          children: (children) =>
            updatePartnersChildren(children, 'PartnersTemplateThree'),
        },
        PartnersTemplateFour: {
          children: (children) =>
            updatePartnersChildren(children, 'PartnersTemplateFour'),
        },
      };
    }

    return null;
  },
  // Fix bad migration in convertPercentInPixels which created a bug in the padding of the sections
  // Example: for the 0% we get spaces in the containerPadding => "0% 0% 5% 0%" to "  47px "
  ({ document }: PageSchemaBuilder<BuilderComponent>) => {
    if (needMigration(document, '2024-09-30')) {
      return {
        Section: {
          attributes: () => ({
            containerPadding: (padding: string) => {
              if (!padding) {
                return '0px';
              }
              const splitedValue = padding.split(' ');
              const containerPadding: Array<string> = [];

              splitedValue.forEach((element) => {
                if (element === '') {
                  containerPadding.push('0px');
                } else {
                  containerPadding.push(element);
                }
              });

              return containerPadding.join(' ');
            },
          }),
        },
      };
    }

    return null;
  },
];

export default schemas;
